import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';

export interface BossPhase {
  hpPercent: number;
  pattern: string;
  dropOnEnter?: string;
  [key: string]: unknown;
}

export interface PhaseConfig extends BehaviorConfig {
  phases?: BossPhase[];
}

export interface PhaseChangeEvent {
  boss: Entity;
  phase: BossPhase;
  phaseIndex: number;
  oldPhase?: BossPhase;
}

/**
 * PhaseBehavior - Manages boss phase transitions
 *
 * Used by: Boss entities
 *
 * Features:
 * - Tracks phases based on HP percentage
 * - Triggers phase transitions automatically
 * - Can drop items on phase change
 * - Emits events for phase changes
 */
export class PhaseBehavior extends Behavior {
  public phases: BossPhase[];
  public currentPhaseIndex: number = 0;

  constructor(entity: Entity, config: PhaseConfig = {}) {
    super(entity, config);

    // Sort phases by HP descending (100% first, 25% last)
    this.phases = (config.phases || []).sort((a, b) => b.hpPercent - a.hpPercent);
  }

  /**
   * Get current phase
   */
  get currentPhase(): BossPhase | undefined {
    return this.phases[this.currentPhaseIndex];
  }

  /**
   * Check if we should transition to a new phase
   */
  checkTransition(currentHp: number, maxHp: number): void {
    if (this.phases.length === 0) return;

    const hpPercent = (currentHp / maxHp) * 100;

    // Find the appropriate phase for current HP
    for (let i = this.currentPhaseIndex; i < this.phases.length; i++) {
      const phase = this.phases[i];

      // If HP is at or below this phase's threshold and we haven't entered it yet
      if (hpPercent <= phase.hpPercent && i > this.currentPhaseIndex) {
        this.transitionTo(i);
        break;
      }
    }
  }

  /**
   * Transition to a specific phase
   */
  transitionTo(phaseIndex: number): void {
    if (phaseIndex < 0 || phaseIndex >= this.phases.length) return;
    if (phaseIndex === this.currentPhaseIndex) return;

    const oldPhase = this.currentPhase;
    this.currentPhaseIndex = phaseIndex;
    const newPhase = this.currentPhase;

    if (!newPhase) return;

    // Call entity callback if exists
    if (typeof (this.entity as Entity & { onPhaseChange?: (newPhase: BossPhase, oldPhase?: BossPhase) => void }).onPhaseChange === 'function') {
      (this.entity as Entity & { onPhaseChange: (newPhase: BossPhase, oldPhase?: BossPhase) => void }).onPhaseChange(newPhase, oldPhase);
    }

    // Drop item if phase has dropOnEnter
    if (newPhase.dropOnEnter) {
      this.scene.events.emit('spawnDrop', {
        type: newPhase.dropOnEnter,
        x: this.entity.x,
        y: this.entity.y
      });
    }

    // Emit global event
    this.scene.events.emit('bossPhaseChange', {
      boss: this.entity,
      phase: newPhase,
      phaseIndex: phaseIndex,
      oldPhase: oldPhase
    } as PhaseChangeEvent);
  }

  /**
   * Force transition to a phase by pattern name
   */
  transitionToPattern(pattern: string): void {
    const index = this.phases.findIndex(p => p.pattern === pattern);
    if (index !== -1) {
      this.transitionTo(index);
    }
  }

  /**
   * Get remaining phases
   */
  getRemainingPhases(): BossPhase[] {
    return this.phases.slice(this.currentPhaseIndex + 1);
  }

  /**
   * Check if this is the final phase
   */
  isFinalPhase(): boolean {
    return this.currentPhaseIndex >= this.phases.length - 1;
  }

  /**
   * Reset to first phase
   */
  reset(): void {
    this.currentPhaseIndex = 0;
  }
}
